import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material'
import { Button, Dialog, IconButton } from '@mui/material'
import { useContext, useState } from 'react'
import { regenerateMicroLearning } from '../../../api/api'
import { FeedbackCategoryId, MicroLearningDetailResponse } from '../../../api/models'
import { AppStateContext } from '../../../state/AppProvider'
import { MicroLearningActionModal } from './MicroLearningActionModal'
import styles from './MicroLearningActionModal/MicroLearningActionModal.module.css'
import RegenerateModalContent from './RegenerateModalContent'

type RegenerateModalProps = {
  open: boolean
  onClose: () => void
  mlId: string
  data: MicroLearningDetailResponse | null
  onSuccess: () => void
}

export default function RegenerateModal({ open, onClose, mlId, data, onSuccess }: RegenerateModalProps) {
  const appStateContext = useContext(AppStateContext)
  const [selectedCategories, setSelectedCategories] = useState<FeedbackCategoryId[]>([])
  const [feedbackText, setFeedbackText] = useState('')
  const feedbackMaxLength = appStateContext?.state.frontendSettings?.micro_learning?.summary_max_length ?? 500
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showApprovalWarning, setShowApprovalWarning] = useState(false)

  const isApproved = data?.status === 'approved'

  const handleClose = () => {
    setSelectedCategories([])
    setFeedbackText('')
    setShowApprovalWarning(false)
    onClose()
  }

  const handleToggleCategory = (id: FeedbackCategoryId) => {
    setSelectedCategories(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]))
  }

  const handleConfirm = async () => {
    if (!mlId || selectedCategories.length === 0 || !feedbackText.trim()) return
    setIsSubmitting(true)
    const result = await regenerateMicroLearning(mlId, {
      categories: selectedCategories,
      text: feedbackText.trim(),
    })
    setIsSubmitting(false)

    if (result.success) {
      handleClose()
      onSuccess()
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: {
          message: `Regeneration started for ${mlId}. Check back soon for the updated version.`,
          severity: 'success',
        },
      })
    } else {
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: {
          message: 'Failed to start regeneration. Try again later.',
          severity: 'warning',
        },
      })
    }
  }

  // If ML is approved, show warning first; otherwise show feedback modal directly
  const showFeedbackModal = open && (!isApproved || showApprovalWarning)
  const showWarningDialog = open && isApproved && !showApprovalWarning

  return (
    <>
      <Dialog
        open={showWarningDialog}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '16px' } } }}>
        <div className={styles.warningContainer}>
          <IconButton aria-label='close' onClick={handleClose} className={styles.warningCloseButton}>
            <CloseIcon />
          </IconButton>
          <div className={styles.warningTitle}>Attention</div>
          <div className={styles.warningContent}>
            This micro-learning is currently <strong>approved</strong>. Refine & Regenerate will revert it to Pending
            Review until re-approved.
          </div>
          <div className={styles.warningFooter}>
            <Button variant='outlined' onClick={handleClose} className={styles.outlinedCancelButton}>
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={() => setShowApprovalWarning(true)}
              className={styles.confirmButton}>
              Continue
            </Button>
          </div>
        </div>
      </Dialog>

      <MicroLearningActionModal
        open={showFeedbackModal}
        onClose={handleClose}
        onConfirm={handleConfirm}
        confirmText='Submit & Regenerate'
        confirmDisabled={selectedCategories.length === 0 || !feedbackText.trim() || isSubmitting}
        theme='amber'
        headerIcon={<RefreshIcon style={{ color: '#E65100', fontSize: 20 }} />}
        title='Refine & Regenerate'
        subtitle='Provide feedback for the AI to regenerate this micro-learning'
        mlId={data?.ml_id || '—'}
        mlTitle={data?.generation?.title || data?.title || 'Micro-Learning'}
        score={data?.score}>
        <RegenerateModalContent
          selectedCategories={selectedCategories}
          onToggleCategory={handleToggleCategory}
          feedbackText={feedbackText}
          onFeedbackTextChange={value => {
            if (value.length <= feedbackMaxLength || value.length < feedbackText.length) {
              setFeedbackText(value)
            }
          }}
          feedbackMaxLength={feedbackMaxLength}
        />
      </MicroLearningActionModal>
    </>
  )
}
